
import React from "react";
import { Layout, Collapse, Typography, Divider } from "antd";
import {
  QuestionCircleOutlined,
  FolderOpenOutlined,
  ThunderboltOutlined,
  UserOutlined,
  PlusCircleOutlined,
} from "@ant-design/icons";
import { useMsal } from "@azure/msal-react";
import { Header } from "../components/layout/header";

const { Content } = Layout;
const { Title, Paragraph } = Typography;

const faqData = [
  {
    key: "storage",
    icon: <FolderOpenOutlined />,
    title: "Storage & Retention",
    items: [
      {
        key: "persist-notes",
        q: "Do scratch notes persist after I log out?",
        a: `Scratch notes are temporarily saved to AdvancedMD and automatically deleted after seven days or when you submit the note to the EHR, whichever occurs first.`,
      },
      {
        key: "submission-sign",
        q: "Does submission sign or finalize the note?",
        a: `No. Submission transfers content to AdvancedMD. Signing the note is only possible through the AMD portal.`,
      },
      {
        key: "sync-back",
        q: "Will edits made in AdvancedMD sync back to the app?",
        a: `No. Updates made in AdvancedMD may not sync back to the app. After submission, any final edits should be made in AdvancedMD.`,
      },
    ],
  },
  {
    key: "functionality",
    icon: <ThunderboltOutlined />,
    title: "Functionality & Behavior",
    items: [
      {
        key: "recording-length",
        q: "How long can a single recording be?",
        a: `Recordings can range from a few seconds to several hours, depending on the duration of the conversation.`,
      },
      {
        key: "nothing-after-record",
        q: "What if nothing appears after I hit record or upload?",
        a: `Transcriptions should begin appearing within about 10 seconds of hitting record. If they do not, refresh the browser or clear your cache. Reopen the application until the transcript or recording starts.`,
      },
      {
        key: "ai-notes-empty",
        q: "I hit “Generate AI Notes” but nothing appeared.",
        a: `Sometimes the AI Notes tab may not refresh automatically. Wait about a minute, then refresh the page and navigate to the AI Notes tab again — the generated note should appear.`,
      },
      {
        key: "edits-disappear",
        q: "Why did my previous edits disappear?",
        a: `When you regenerate a note, the app rebuilds it using your transcript and scratch notes, replacing any unsubmitted edits. Pressing “Generate AI Notes” again will overwrite unsubmitted changes.`,
      },
      {
        key: "autosave",
        q: "Is there autosave?",
        a: `Not for the AI Note itself. The transcript and scratch notes are saved, but edits made directly to the AI Note are not. Use Send to EHR to save your work in the AdvancedMD portal and continue there.`,
      },
      {
        key: "why-notes-not-saved",
        q: "Why are the transcript and scratch notes saved, but not the AI Notes/Template?",
        a: `AI-generated content requires your explicit permission before it can be saved to the EHR for compliance and privacy reasons.`,
      },
      {
        key: "back-and-work",
        q: "If I hit “Send to EHR,” can I still come back and work on the note?",
        a: `Yes. You can send to EHR multiple times, but after submission, the Generate AI Notes feature will no longer be available for that note.`,
      },
      {
        key: "format-change",
        q: "Can I change the way notes are formatted?",
        a: `Yes. You can switch between bullet point or paragraph format in Settings.`,
      },
      {
        key: "browsers",
        q: "What browsers are supported?",
        a: `The application works best on all major web browsers. If a transcript does not appear, refresh the page and try again.`,
      },
      {
        key: "generation-speed",
        q: "How fast are AI notes generated?",
        a: `AI notes typically generate within about one minute, depending on your device and internet connection.`,
      },
    ],
  },
  {
    key: "interaction",
    icon: <UserOutlined />,
    title: "User Interaction",
    items: [
      {
        key: "edit-before-submit",
        q: "Can I edit the AI-generated note before submitting?",
        a: `Yes. You can review and modify the generated note before submitting it to the EHR.`,
      },
      {
        key: "inaccurate-note",
        q: "What should I do if the AI note seems inaccurate or incomplete?",
        a: `Review the transcript and scratch notes for any missing or unclear details, then regenerate or manually edit the note if needed.`,
      },
      {
        key: "multi-user",
        q: "Does the app support multiple users or shared access?",
        a: `Each user session is individual. Data is not shared between users unless submitted to the EHR as part of normal workflow.`,
      },
      {
        key: "secure",
        q: "Is patient data secure during processing?",
        a: `Yes. All data is processed securely and handled according to your organization’s privacy and compliance standards.`,
      },
    ],
  },
  {
    key: "additional",
    icon: <PlusCircleOutlined />,
    title: "Additional FAQs",
    items: [
      {
        key: "short-recordings",
        q: "Will short recordings work?",
        a: `Yes. Even short recordings can generate a complete note. However, if the recording contains no relevant medical content, it may not produce a note. Always review before submission.`,
      },
      {
        key: "restart-or-network",
        q: "What happens if my device restarts or the network disconnects?",
        a: `Unsaved edits may be lost if your device restarts or loses connection. Reopen the session, confirm the transcript is available, and regenerate the AI notes if needed.`,
      },
      {
        key: "undo",
        q: "Can I undo my action after clicking “Generate AI Notes”?",
        a: `There is no undo option. You can regenerate the AI notes again from your transcript and scratch notes.`,
      },
      {
        key: "auto-filled-sections",
        q: "Which sections can be auto-filled from transcripts and scratch notes?",
        a: `The following sections can populate automatically: Chief Complaint, HPI, Substance Use, Medications, Diagnosis, Treatment Plan, and Patient Education. Medication names and dosages appear automatically where applicable.`,
      },
      {
        key: "typical-time",
        q: "What’s the typical note generation time?",
        a: `On average, AI note generation takes around one minute, depending on your device performance and network speed.`,
      },
    ],
  },
];

export default function FAQ({
    selectedDoctorId,
    setSelectedDoctorId,
    selectedDate,
    setSelectedDate,
    options
}) {
  const { instance } = useMsal();

  const logout = () => {
    instance.logout();
    sessionStorage.clear();
  };

  return (
    <Layout className="flex flex-col h-screen">
      <Header
         onLogout={logout}
         selectedDoctorId={selectedDoctorId}
         onSelectDoctor={setSelectedDoctorId}
         options={options}
      />
      <Content className="w-full flex flex-col items-center py-10 overflow-y-auto custom-scrollbar">
        <div className="w-full max-w-5xl px-4">
          <Title
            level={3}
            style={{ marginTop: 0, display: "flex", alignItems: "center", gap: 8 }}
          >
            <QuestionCircleOutlined />
            Frequently Asked Questions (FAQs)
          </Title>
          <Paragraph type="secondary" style={{ marginBottom: 20 }}>
            Click a question to expand the answer.
          </Paragraph>

          {faqData.map((section, idx) => (
            <div key={section.key} className="mb-6">
              <Title
                level={5}
                style={{ marginBottom: 12, display: "flex", alignItems: "center", gap: 8 }}
              >
                {section.icon}
                {section.title}
              </Title>

              <Collapse
                accordion={false}
                bordered
                expandIconPosition="end"
                items={section.items.map((it) => ({
                  key: it.key,
                  label: <span className="font-medium ">{it.q}</span>,
                  children: <Paragraph style={{ marginBottom: 0 , padding: "0px 12px"}}>{it.a}</Paragraph>,
                }))}
                className="rounded-md"
              />

              {idx < faqData.length - 1 && <Divider style={{ marginTop: 20 }} />}
            </div>
          ))}
        </div>
      </Content>
    </Layout>
  );
}
